/**
 * Registers the service worker through vite-plugin-pwa's virtual module.
 *
 * Production only (or `VITE_PWA_DEV=1`), skipped with `VITE_PWA_DISABLE=1`, and
 * it never throws: a browser without service workers, a blocked registration
 * or a missing virtual module all end in `null`. Updates are never applied by
 * themselves: `onNeedRefresh` fires and the UI (PwaUpdater) decides.
 */

/** Applies the waiting worker; `true` also reloads the page. */
export type UpdateServiceWorker = (reloadPage?: boolean) => Promise<void>;

export interface RegisterServiceWorkerOptions {
  /** A new version is installed and waiting. */
  onNeedRefresh: () => void;
  /** The app shell is cached for offline use (first install). */
  onOfflineReady?: () => void;
}

/** How often an open tab asks the server for a newer worker. */
export const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;

export function isServiceWorkerEnabled(): boolean {
  try {
    const env = import.meta.env;
    if (env.VITE_PWA_DISABLE === '1') {
      return false;
    }
    if (!env.PROD && env.VITE_PWA_DEV !== '1') {
      return false;
    }
    return typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
  } catch {
    return false;
  }
}

export async function registerServiceWorker(
  options: RegisterServiceWorkerOptions
): Promise<UpdateServiceWorker | null> {
  try {
    if (!isServiceWorkerEnabled()) {
      return null;
    }
    const { registerSW } = await import('virtual:pwa-register');
    return registerSW({
      onNeedRefresh: options.onNeedRefresh,
      onOfflineReady: options.onOfflineReady,
      onRegisteredSW(_url, registration) {
        if (!registration) {
          return;
        }
        setInterval(() => {
          if (navigator.onLine) {
            registration.update().catch(() => undefined);
          }
        }, UPDATE_CHECK_INTERVAL_MS);
      },
    });
  } catch {
    return null;
  }
}
